import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, CircleAlertIcon, LockKeyholeIcon } from 'lucide-react';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { Link } from './Link';
import { Logo } from './Logo';
import { TextInput } from './TextInput';
type BrandMessage = {
  title: string;
  description: string;
};
type FieldErrors = {
  identity?: string;
  password?: string;
};
type Feedback = {
  type: 'success';
  message: string;
} | {
  type: 'error';
  message: string;
} | null;
type LoginScreenProps = {
  onLogin: (username: 'max' | 'maxpro') => void;
};
const brandMessages: BrandMessage[] = [{
  title: 'Präzision in jeder Charge.',
  description: 'Rezepturen, Aufträge und geführte Arbeitsschritte – klar verbunden für eine Produktion, die zuverlässig weiterläuft.'
}, {
  title: 'Probleme mit der Software?',
  description: 'Sie erreichen uns unter +49 5401 1689580.'
}, {
  title: 'Alles im Blick.',
  description: 'Behalten Sie Produktionsschritte, Rohstoffe und Aufträge jederzeit im Überblick.'
}];
export function LoginScreen({
  onLogin
}: LoginScreenProps) {
  const [identity, setIdentity] = useState('');
  const [password, setPassword] = useState('');
  const [rememberCredentials, setRememberCredentials] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [feedback, setFeedback] = useState<Feedback>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeBrandMessageIndex, setActiveBrandMessageIndex] = useState(0);
  const activeBrandMessage = brandMessages[activeBrandMessageIndex];
  useEffect(() => {
    const rotationInterval = window.setInterval(() => {
      setActiveBrandMessageIndex(currentIndex => (currentIndex + 1) % brandMessages.length);
    }, 5000);
    return () => window.clearInterval(rotationInterval);
  }, []);
  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors: FieldErrors = {};
    if (!identity.trim()) {
      nextErrors.identity = 'Bitte geben Sie Ihren Benutzernamen oder Ihre E-Mail-Adresse ein.';
    }
    if (!password) {
      nextErrors.password = 'Bitte geben Sie Ihr Passwort ein.';
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      setFeedback({
        type: 'error',
        message: 'Ihre Anmeldung konnte noch nicht gestartet werden. Prüfen Sie die markierten Felder.'
      });
      return;
    }
    const normalizedIdentity = identity.trim().toLowerCase();
    if (normalizedIdentity !== 'max' && normalizedIdentity !== 'maxpro' || password !== '123') {
      setFeedback({
        type: 'error',
        message: 'Die Anmeldedaten sind nicht korrekt. Bitte versuchen Sie es erneut.'
      });
      return;
    }
    setErrors({});
    setFeedback(null);
    setIsLoading(true);
    window.setTimeout(() => {
      setIsLoading(false);
      onLogin(normalizedIdentity);
    }, 900);
  }
  return <main className="min-h-screen w-full bg-surface font-body text-text-primary md:grid md:grid-cols-[42%_58%] lg:grid-cols-[45%_55%]" data-id="element-3">
      <motion.aside aria-label="Sonnentracht Produktionssoftware" className="relative isolate flex min-h-[320px] overflow-hidden bg-surface-subtle px-6 py-8 sm:px-10 sm:py-10 md:min-h-screen md:px-8 md:py-10 lg:px-12 lg:py-12 xl:px-16" initial={{
      opacity: 0,
      x: -18
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.45,
      ease: 'easeOut'
    }} data-id="element-4">
        <img src="https://cdn.magicpatterns.com/uploads/q1rBvg1xNaTmX89pDE8usJ/ChatGPT_Image_10._Juli_2026,_12_59_11.png" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" data-id="element-5" />
        <div className="pointer-events-none absolute inset-0 bg-surface-subtle/45" data-id="element-6" />
        <div className="pointer-events-none absolute inset-y-0 right-0 hidden w-[58%] md:block" style={{
        background: 'linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(247,247,245,0.18) 38%, rgba(255,255,255,0.68) 72%, #ffffff 100%)'
      }} data-id="element-7" />

        <div className="relative flex w-full flex-col justify-between" data-id="element-8">
          <header data-id="element-9">
            <Logo size="lg" alt="Sonnentracht" className="h-[75px] w-auto" data-id="element-10" />
          </header>

          <section className="mt-12 max-w-md rounded-xl border border-white/70 bg-surface-subtle/90 p-5 shadow-sm backdrop-blur-sm md:mt-auto md:mb-8 lg:mb-12 lg:p-6" aria-labelledby="brand-message" data-id="element-11">
            <AnimatePresence mode="wait" initial={false} data-id="element-12">
              <motion.div key={activeBrandMessageIndex} initial={{
              opacity: 0,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.2,
              ease: 'easeOut'
            }} data-id="element-13">
                <h2 id="brand-message" className="font-heading text-3xl leading-tight text-text-primary sm:text-4xl" data-id="element-14">
                  {activeBrandMessage.title}
                </h2>
                <p className="mt-4 max-w-sm text-base leading-7 text-text-primary" data-id="element-15">
                  {activeBrandMessage.description}
                </p>
              </motion.div>
            </AnimatePresence>

            <div className="mt-7 border-t border-border pt-5" aria-label={`Hinweis ${activeBrandMessageIndex + 1} von ${brandMessages.length}`} data-id="element-16">
              <div className="flex items-center gap-3" data-id="element-17">
                {brandMessages.map((message, index) => {
                const isActive = index === activeBrandMessageIndex;
                return <span key={message.title} className={`h-2.5 rounded-full transition-[width,background-color] duration-200 ${isActive ? 'w-10 bg-brand-green' : 'w-2.5 bg-border'}`} aria-hidden="true" data-id="element-18" />;
              })}
              </div>
            </div>
          </section>
        </div>
      </motion.aside>

      <section className="flex min-h-[calc(100vh-320px)] items-center justify-center bg-surface px-6 py-10 sm:px-10 md:min-h-screen md:px-10 lg:px-16 xl:px-24" data-id="element-19">
        <motion.div className="w-full max-w-[430px] md:max-w-[400px] lg:max-w-[430px]" initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.12,
        ease: 'easeOut'
      }} data-id="element-20">
          <header className="mb-9" data-id="element-21">
            <h1 className="font-heading text-4xl leading-tight sm:text-5xl" data-id="element-22">
              Willkommen zurück
            </h1>
            <p className="mt-3 text-base text-text-secondary" data-id="element-23">
              Melden Sie sich an, um Ihren Arbeitsbereich zu öffnen.
            </p>
          </header>

          <form noValidate onSubmit={handleSubmit} aria-describedby="login-feedback" data-id="element-24">
            <div className="space-y-5" data-id="element-25">
              <TextInput id="identity" name="identity" label="E-Mail oder Benutzername" type="text" autoComplete="username" placeholder="name@unternehmen.de" value={identity} onChange={event => setIdentity(event.target.value)} error={errors.identity} required disabled={isLoading} data-id="element-26" />

              <TextInput id="password" name="password" label="Passwort" type="password" autoComplete="current-password" placeholder="••••••••" value={password} onChange={event => setPassword(event.target.value)} error={errors.password} required disabled={isLoading} data-id="element-27" />
            </div>

            <div className="mt-5 flex items-center justify-between gap-4" data-id="element-28">
              <div className="flex min-h-[48px] items-center" data-id="element-29">
                <Checkbox id="remember-credentials" name="rememberCredentials" checked={rememberCredentials} onCheckedChange={setRememberCredentials} label="Anmeldedaten merken" disabled={isLoading} data-id="element-30" />
              </div>
              <Link href="#passwort-zuruecksetzen" className="shrink-0 text-sm" data-id="element-31">
                Passwort vergessen?
              </Link>
            </div>

            <Button type="submit" size="lg" loading={isLoading} className="mt-7 w-full min-h-[56px] justify-center text-base" leadingIcon={isLoading ? undefined : <LockKeyholeIcon size={18} aria-hidden="true" data-id="element-33" />} data-id="element-32">
              {isLoading ? 'Anmeldung wird vorbereitet …' : 'Anmelden'}
            </Button>

            <div id="login-feedback" className="mt-4" aria-live="polite" aria-atomic="true" data-id="element-34">
              <AnimatePresence mode="wait" data-id="element-35">
                {feedback && <motion.div key={feedback.type} role={feedback.type === 'error' ? 'alert' : 'status'} className={`flex items-start gap-3 rounded-md border px-4 py-3 text-sm leading-5 ${feedback.type === 'success' ? 'border-success bg-surface-subtle text-text-primary' : 'border-error bg-surface-subtle text-text-primary'}`} initial={{
                opacity: 0,
                y: -6
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -4
              }} transition={{
                duration: 0.2
              }} data-id="element-36">
                    {feedback.type === 'success' ? <CheckCircle2Icon className="mt-0.5 shrink-0 text-success" size={18} aria-hidden="true" data-id="element-37" /> : <CircleAlertIcon className="mt-0.5 shrink-0 text-error" size={18} aria-hidden="true" data-id="element-38" />}
                    <p data-id="element-39">{feedback.message}</p>
                  </motion.div>}
              </AnimatePresence>
            </div>
          </form>

          <p className="mt-9 text-center text-xs leading-5 text-text-secondary" data-id="element-40">
            Geschützter Zugang für autorisierte Mitarbeitende.
          </p>
        </motion.div>
      </section>
    </main>;
}